import WebApp from '@twa-dev/sdk';
import { getCurrentEnvironment } from './environment';

// Universal modal utilities that work in both TMA and PWA
export const modalUtils = {
  // Show confirmation dialog
  showConfirm: (message: string, onConfirm: (confirmed: boolean) => void) => {
    const environment = getCurrentEnvironment();
    
    if (environment === 'telegram') {
      // Use Telegram WebApp API
      WebApp.showConfirm(message, onConfirm);
    } else {
      // Use browser confirm for PWA
      const confirmed = window.confirm(message);
      onConfirm(confirmed);
    }
  },

  // Show alert dialog
  showAlert: (message: string) => {
    const environment = getCurrentEnvironment();
    
    if (environment === 'telegram') {
      // Use Telegram WebApp API
      WebApp.showAlert(message);
    } else {
      // Use browser alert for PWA
      window.alert(message);
    }
  },

  // Show prompt dialog (only available in PWA, not in Telegram)
  showPrompt: (message: string, defaultValue?: string): string | null => {
    const environment = getCurrentEnvironment();
    
    if (environment === 'telegram') {
      // Telegram WebApp doesn't have showPrompt, use alert instead
      WebApp.showAlert(message);
      return null;
    } else {
      // Use browser prompt for PWA
      return window.prompt(message, defaultValue);
    }
  },

  // Show success popup with custom message and navigation
  showSuccessPopup: (title: string, message: string, onOk?: () => void) => {
    const environment = getCurrentEnvironment();
    
    if (environment === 'telegram') {
      // Use Telegram WebApp API
      WebApp.showPopup({
        title,
        message,
        buttons: [{ type: 'ok' }]
      });
      // Call onOk callback after a short delay to allow popup to show
      if (onOk) {
        setTimeout(onOk, 100);
      }
    } else {
      // Use browser alert for PWA, then call onOk
      window.alert(`${title}\n\n${message}`);
      if (onOk) {
        onOk();
      }
    }
  },

  // Show custom popup with multiple buttons (for complex scenarios)
  showCustomPopup: (
    title: string, 
    message: string, 
    buttons: Array<{ text: string; action: () => void; primary?: boolean }>
  ) => {
    const environment = getCurrentEnvironment();
    
    if (environment === 'telegram') {
      // Use Telegram WebApp API with button mapping
      const telegramButtons = buttons.map((button, index) => ({
        type: (button.primary ? 'default' : 'destructive') as 'default' | 'destructive',
        id: `btn-${index}`,
        text: button.text
      }));
      
      WebApp.showPopup({
        title,
        message,
        buttons: telegramButtons
      });
      
      // Note: Telegram WebApp doesn't provide individual button callbacks
      // So we'll use a simple approach - call the first action for 'ok' type buttons
      // For more complex scenarios, you might need to implement a custom popup component
      if (buttons.length === 1) {
        setTimeout(buttons[0].action, 100);
      }
    } else {
      // For PWA, create a custom popup using browser APIs
      const result = window.confirm(`${title}\n\n${message}\n\nClick OK to continue or Cancel to decline.`);
      if (result && buttons.length > 0) {
        buttons[0].action();
      } else if (!result && buttons.length > 1) {
        buttons[1].action();
      }
    }
  }
}; 